import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useTourState } from '@/hooks/useTourState';

const WELCOME_TOUR_ID = 'welcome';
const TARGET_SELECTOR = '[data-tour-id="help-button"]';
const TARGET_RETRY_ATTEMPTS = 15;
const TARGET_RETRY_INTERVAL_MS = 100;

/**
 * One-time coach mark pointing at the global `?` HelpButton.
 *
 * First visit only: spotlights the HelpButton and prompts the user once.
 * Subsequent visits do nothing — page-specific tours are launched by clicking `?`.
 * Dismiss writes status='dismissed' to useTourState('welcome') (with localStorage
 * fallback pre-deploy) so the nudge never re-appears after acknowledgement.
 */
export function WelcomeNudge() {
  const reduceMotion = useReducedMotion();
  const { data, isSuccess, markDismissed } = useTourState(WELCOME_TOUR_ID);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only first-visit: data === null means no row/cache for 'welcome'
    if (!isSuccess || data !== null || visible) return;

    let cancelled = false;
    let attempts = 0;
    const tryResolve = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(TARGET_SELECTOR);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        setVisible(true);
        return;
      }
      attempts++;
      if (attempts < TARGET_RETRY_ATTEMPTS) {
        setTimeout(tryResolve, TARGET_RETRY_INTERVAL_MS);
      }
    };
    tryResolve();
    return () => {
      cancelled = true;
    };
  }, [isSuccess, data, visible]);

  // Recompute position on resize so the coach mark stays anchored to the button
  useEffect(() => {
    if (!visible) return;
    const update = () => {
      const el = document.querySelector<HTMLElement>(TARGET_SELECTOR);
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [visible]);

  if (!visible || !targetRect) return null;

  const handleDismiss = () => {
    markDismissed();
    setVisible(false);
  };

  // Position the card below the button, right-aligned to the viewport
  const cardTop = targetRect.bottom + 16;
  const cardRight = Math.max(16, window.innerWidth - targetRect.right - 4);

  const entranceProps = reduceMotion
    ? { initial: false as const }
    : {
        initial: { scale: 0.95, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        transition: { duration: 0.2, ease: 'easeOut' as const },
      };

  return (
    <>
      {/* Spotlight ring + box-shadow dim-around */}
      <div
        className="fixed pointer-events-none rounded-full ring-[3px] ring-secondary"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
          zIndex: 10000,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65), 0 0 24px 2px hsl(var(--secondary) / 0.4)',
        }}
        aria-hidden="true"
      />

      {/* Coach mark card */}
      <motion.div
        role="dialog"
        aria-labelledby="welcome-nudge-title"
        aria-modal="false"
        className="fixed bg-surface-elevated border border-border rounded-lg shadow-[0_20px_40px_rgba(0,0,0,0.4)] p-5 w-[320px] max-w-[calc(100vw-32px)]"
        style={{ top: cardTop, right: cardRight, zIndex: 10001 }}
        {...entranceProps}
      >
        <h2 id="welcome-nudge-title" className="text-lg font-semibold text-foreground mb-1">
          Need a quick tour?
        </h2>
        <p className="text-sm font-normal leading-relaxed text-foreground">
          Click this button on any page to see a guided tour of what's on screen.
        </p>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleDismiss} className="text-sm font-semibold min-h-11">
            Got it
          </Button>
        </div>
      </motion.div>
    </>
  );
}
